import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Award, BookOpen, Globe, Mail, Linkedin, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";

const Consultants = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 100);
    return () => clearTimeout(t);
  }, [hash]);
  const consultants = [
    {
      id: "marshalee-valentine",
      name: "Marshalee Valentine Gordon",
      title: "Lead Consultant & Managing Director",
      image: "https://customer-assets.emergentagent.com/job_bf218fcd-c125-458a-9a30-8b4eb7596b49/artifacts/z5ved132_mv_2%202.jpg",
      credentials: [
        "PECB Certified ISO 9001 Senior Lead Auditor",
        "ISO 9001 Consultant",
        "PECB Certified ISO 9001 Trainer",
        "PECB Certified ISO 31000 Senior Lead Risk Manager"
      ],
      expertise: [
        "Quality Management Systems",
        "Risk Management",
        "Internal Auditing",
        "Regulatory Compliance",
        "Supplier Auditing"
      ],
      bio: "Marshalee Valentine Gordon is the founding force behind Compliance Management Systems. With over 15 years of experience in quality and risk management, she has helped hundreds of organizations across the Caribbean achieve and maintain internationally recognized certifications. Her practical, hands-on approach to consulting has made her a trusted advisor to manufacturers, processors, and service organizations throughout Jamaica, the wider Caribbean region, and the United States. She contributed to the work of ISO/TC 176/SC 2/WG 29, the international working group responsible for the revision of ISO 9001, and serves as a third-party lead auditor for certification bodies in the Caribbean and the United States. Now based in Madison, Mississippi, she supports clients across the US and the Caribbean.",
      linkedin: "https://www.linkedin.com/in/valentinem/",
      industries: ["Food Manufacturing", "Agri-Business", "Hospitality", "Export Services"]
    },
    {
      id: "loron-pinnock-brown",
      name: "Dr. Loron Pinnock Brown",
      title: "Senior Consultant & Technical Advisor",
      image: "https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&w=400",
      credentials: [
        "Ph.D. in Food Science",
        "PECB Certified ISO 22000 Lead Implementer",
        "PECB Certified ISO 9001 Lead Implementer",
        "GlobalGAP Certified Farm Assurer"
      ],
      expertise: [
        "Agricultural Standards & GAP",
        "Food Science & Technology",
        "Quality System Design",
        "Risk Assessment",
        "Training & Capacity Building"
      ],
      bio: "Dr. Loron Pinnock Brown brings a unique combination of academic rigor and practical industry experience to the CMS team. With a doctorate in Food Science and extensive field experience working with agricultural producers and food processors, he specializes in helping organizations build robust management systems from the ground up. His technical expertise and teaching background make him an exceptional trainer and mentor for organizations seeking to develop internal competence.",
      industries: ["Agriculture", "Food Processing", "Government & Public Sector", "Research Institutions"]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="consultants-page">
      <SEO
        title="Our Consultants | Compliance Management Systems"
        description="Meet our expert consultants specializing in ISO 22000, ISO 9001, HACCP, food safety, and quality management. Jamaica & Caribbean compliance experts."
        url="/consultants"
        keywords={["compliance consultants Jamaica", "ISO consultants Caribbean", "food safety experts", "quality management consultants", "HACCP specialists"]}
      />

      {/* Hero Section */}
      <section className="bg-navy-darker text-white py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award className="h-4 w-4" />
              Expert Team
            </div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-6" data-testid="consultants-title">
              Our Consultants
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Our team of seasoned professionals brings decades of combined experience in quality management, food safety, and compliance. Each consultant is internationally certified and committed to helping your organization achieve excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Consultants Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="space-y-16">
            {consultants.map((consultant, index) => (
              <div 
                key={consultant.id} 
                className={`grid lg:grid-cols-2 gap-12 items-start scroll-mt-28 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                id={consultant.id}
                data-testid={`consultant-${consultant.id}`}
              >
                {/* Image & Quick Info */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <Card className="overflow-hidden shadow-xl">
                    <div className="aspect-[4/3] relative">
                      <img
                        src={consultant.image}
                        alt={consultant.name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-darker/80 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h2 className="font-heading font-bold text-2xl mb-1">{consultant.name}</h2>
                        <p className="text-gold font-medium">{consultant.title}</p>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      {/* Credentials */}
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                          Certifications & Credentials
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {consultant.credentials.map((cred, idx) => (
                            <span 
                              key={idx} 
                              data-testid={`credential-${consultant.id}-${idx}`}
                              className="bg-brand/10 text-brand text-xs font-medium px-3 py-1.5 rounded-full"
                            >
                              {cred}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Industries */}
                      <div>
                        <h4 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                          Industries Served
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {consultant.industries.map((industry, idx) => (
                            <span 
                              key={idx}
                              className="bg-slate-100 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full"
                            >
                              {industry}
                            </span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Bio & Expertise */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="lg:sticky lg:top-28">
                    <h3 className="font-heading font-bold text-2xl text-primary mb-4">
                      About {consultant.name.replace(/^Dr\.\s*/, '').split(' ')[0]}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-8" data-testid={`bio-${consultant.id}`}>
                      {consultant.bio}
                    </p>

                    <div className="bg-slate-50 rounded-xl p-6 mb-8">
                      <h4 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-brand" />
                        Areas of Expertise
                      </h4>
                      <ul className="space-y-3">
                        {consultant.expertise.map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-brand flex-shrink-0" />
                            <span className="text-slate-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Link to="/contact">
                        <Button className="bg-brand hover:bg-brand-dark text-white" data-testid={`contact-${consultant.id}-btn`}>
                          <Mail className="mr-2 h-4 w-4" />
                          Get in Touch
                        </Button>
                      </Link>
                      {consultant.linkedin && (
                        <a href={consultant.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`linkedin-${consultant.id}-btn`}>
                          <Button variant="outline" className="border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2]/5">
                            <Linkedin className="mr-2 h-4 w-4" />
                            LinkedIn Profile
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading font-bold text-3xl text-primary mb-4">
              Why Work With Our Team?
            </h2>
            <p className="text-muted-foreground">
              What sets our consultants apart
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Internationally Certified",
                desc: "All consultants hold multiple PECB certifications and maintain active credentials"
              },
              {
                icon: Globe,
                title: "Regional Expertise",
                desc: "Deep understanding of Caribbean regulatory requirements and market dynamics"
              },
              {
                icon: BookOpen,
                title: "Practical Approach",
                desc: "Focus on implementable solutions, not just theoretical frameworks"
              }
            ].map((item, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-8 pb-6">
                  <div className="w-14 h-14 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-7 w-7 text-brand" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="bg-navy-darker rounded-3xl p-8 lg:p-16 text-center text-white">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-4">
              Ready to Work With Our Experts?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Whether you need consulting, training, or auditing support, our team is ready to help your organization achieve its compliance goals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/services/consulting">
                <Button size="lg" className="bg-gold hover:bg-gold-dark text-navy-darker font-semibold">
                  Request Consulting
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Consultants;
